import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {Box, IconButton} from '@mui/material';
import {DatePicker as MUIDatePicker} from '@mui/x-date-pickers/DatePicker';
import {DateRangePicker} from '@mui/x-date-pickers-pro/DateRangePicker';
import {MultiInputDateRangeField} from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import {DateTime} from 'luxon';
import {GeoJSONFeature} from 'ol/format/GeoJSON';
import {type FC, useCallback, useEffect, useRef, useState} from 'react';
import {useAvailableDates} from '@src/hooks/useAvailableDates';
import useDateTimeParams from '@src/hooks/useDateTimeParams';
import useVisualizationParams from '@src/hooks/useVisualizationParams';
import {isSingleDayDateRange} from '@src/utils/dates';

export type ReduxState = {
  map: {
    extent: [number, number, number, number] | null;
    feature: {
      geometry: GeoJSONFeature;
    };
  };
};

const DatePicker: FC = () => {
  const {layerId} = useVisualizationParams();
  const {parsedDateTimeFrom, parsedDateTimeTo, setDateTimeParams} =
    useDateTimeParams();

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [shouldFetchAvailableDates, setShouldFetchAvailableDates] =
    useState(false);
  const shouldTriggerDateChange = useRef(false);

  const {availableDates, setSearchRange} = useAvailableDates(
    {
      from: parsedDateTimeTo?.startOf('month') ?? null,
      to: parsedDateTimeTo?.endOf('month') ?? null,
    },
    {
      enabled: shouldFetchAvailableDates,
    },
  );

  useEffect(() => {
    if (shouldFetchAvailableDates && availableDates.length > 0) {
      setShouldFetchAvailableDates(false);
    }
  }, [availableDates, shouldFetchAvailableDates]);

  const isSingleDate = isSingleDayDateRange(
    parsedDateTimeFrom,
    parsedDateTimeTo,
  );

  const minDate = DateTime.fromISO('2000-01-01').startOf('day');
  const maxDate = DateTime.now().endOf('day');

  const handleMonthChange = (isRange?: boolean) => (date: DateTime) => {
    setSearchRange({
      from: date.startOf('month'),
      to: isRange ? date.plus({months: 1}).endOf('month') : date.endOf('month'),
    });
    setShouldFetchAvailableDates(true);
  };

  const handleSingleDayDateChange = useCallback(
    (newDateTime: DateTime) => {
      setDateTimeParams({
        dateTimeFrom: newDateTime.startOf('day').toISO(),
        dateTimeTo: newDateTime.endOf('day').toISO(),
      });
    },
    [setDateTimeParams],
  );

  const handleDateRangeChange = (
    newValue: [DateTime | null, DateTime | null],
  ) => {
    const [newFrom, newTo] = newValue;
    if (newFrom && newTo) {
      setDateTimeParams({
        dateTimeFrom: newFrom.startOf('day').toISO(),
        dateTimeTo: newTo.endOf('day').toISO(),
      });
    }
  };

  const getValidRangeFrom = (date?: DateTime): DateTime => {
    if (!date) {
      return parsedDateTimeFrom!;
    }
    if (date < minDate) {
      return minDate.startOf('day');
    } else if (isSingleDate && date > maxDate) {
      return maxDate.startOf('day');
    } else if (!isSingleDate && date > parsedDateTimeTo!) {
      return parsedDateTimeTo!.minus({days: 1});
    }
    return date;
  };

  const getValidRangeTo = (date?: DateTime): DateTime => {
    if (!date) {
      return parsedDateTimeTo!;
    }
    if (date > maxDate) {
      return maxDate.endOf('day');
    } else if (isSingleDate && date < minDate) {
      return minDate.endOf('day');
    } else if (!isSingleDate && date < parsedDateTimeFrom!) {
      return parsedDateTimeFrom!.plus({days: 1});
    }
    return date;
  };

  const setValidDates = ({
    dateFrom,
    dateTo,
  }: {
    dateFrom?: DateTime;
    dateTo?: DateTime;
  }) => {
    setDateTimeParams({
      dateTimeFrom: getValidRangeFrom(dateFrom).toISO(),
      dateTimeTo: getValidRangeTo(dateTo).toISO(),
    });
  };

  const handlePrev = () => {
    if (!parsedDateTimeFrom) {
      return;
    }

    setShouldFetchAvailableDates(true);

    const previousDates = availableDates
      .filter(date => date < parsedDateTimeFrom)
      .sort((a, b) => b.toMillis() - a.toMillis());

    if (previousDates.length > 0) {
      handleSingleDayDateChange(previousDates[0]);
    } else {
      const prevMonth = parsedDateTimeFrom.minus({months: 1}).endOf('month');
      if (prevMonth < minDate) {
        return;
      }
      setSearchRange({
        from: prevMonth.startOf('month'),
        to: prevMonth.endOf('month'),
      });
      shouldTriggerDateChange.current = true;
    }
  };

  const handleNext = () => {
    if (!parsedDateTimeTo) {
      return;
    }

    setShouldFetchAvailableDates(true);

    const nextDates = availableDates
      .filter(date => date > parsedDateTimeTo)
      .sort((a, b) => a.toMillis() - b.toMillis());

    if (nextDates.length > 0) {
      handleSingleDayDateChange(nextDates[0]);
    } else {
      const nextMonth = parsedDateTimeTo.plus({months: 1}).startOf('month');
      if (nextMonth > maxDate) {
        return;
      }
      setSearchRange({
        from: nextMonth.startOf('month'),
        to: nextMonth.endOf('month'),
      });
      shouldTriggerDateChange.current = true;
    }
  };

  useEffect(() => {
    if (!parsedDateTimeTo || !shouldTriggerDateChange.current) {
      return;
    }

    const sortedDates = availableDates.sort(
      (a, b) => b.toMillis() - a.toMillis(),
    );

    const currentDateExists = sortedDates.some(date =>
      date.hasSame(parsedDateTimeTo, 'day'),
    );

    if (sortedDates.length > 0 && !currentDateExists) {
      if (sortedDates[0] < parsedDateTimeTo) {
        handleSingleDayDateChange(sortedDates[0]);
      } else {
        handleSingleDayDateChange(sortedDates[sortedDates.length - 1]);
      }
      shouldTriggerDateChange.current = false;
    }
  }, [availableDates, parsedDateTimeTo, handleSingleDayDateChange]);

  if (!parsedDateTimeFrom || !parsedDateTimeTo) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      {isSingleDate ? (
        <>
          <IconButton
            onClick={handlePrev}
            aria-label="Previous day"
            sx={{color: 'primary.main'}}
            disabled={parsedDateTimeTo <= minDate || !layerId}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>
          <MUIDatePicker
            format="yyyy-LL-dd"
            timezone="UTC"
            label="Date"
            open={openDatePicker}
            onClose={() => setOpenDatePicker(false)}
            slotProps={{
              textField: {
                error: false,
                size: 'small',
                onClick: () => {
                  setShouldFetchAvailableDates(true);
                  setOpenDatePicker(true);
                },
                onBlur: () =>
                  setValidDates({
                    dateFrom: parsedDateTimeFrom,
                    dateTo: parsedDateTimeTo,
                  }),
              },
              openPickerButton: {
                sx: {
                  display: 'none',
                },
              },
              day: ownerState => {
                const isAvailable = availableDates?.some(availableDate =>
                  availableDate.hasSame(ownerState.day, 'day'),
                );

                return {
                  sx: {
                    ...(!isAvailable && {
                      color: 'text.disabled',
                      opacity: 0.5,
                    }),
                  },
                };
              },
            }}
            value={parsedDateTimeTo}
            onChange={newValue =>
              handleSingleDayDateChange(newValue as DateTime)
            }
            onMonthChange={handleMonthChange()}
            minDate={minDate}
            maxDate={maxDate}
          />

          <IconButton
            onClick={handleNext}
            aria-label="Next day"
            sx={{color: 'primary.main'}}
            disabled={parsedDateTimeTo >= maxDate || !layerId}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </>
      ) : (
        <DateRangePicker
          format="yyyy-LL-dd"
          timezone="UTC"
          slots={{
            field: MultiInputDateRangeField,
          }}
          slotProps={{
            // @ts-expect-error type issue with MUI
            textField: ({position}) => ({
              error: false,
              size: 'small',
              label:
                position === 'start' ? 'Start date (UTC)' : 'End date (UTC)',
              onBlur: () =>
                setValidDates({
                  dateFrom:
                    position === 'start' ? parsedDateTimeFrom : undefined,
                  dateTo: position === 'end' ? parsedDateTimeTo : undefined,
                }),
            }),
            field: {
              // @ts-expect-error type issue with MUI
              sx: {
                width: '100%',
              },
            },
            day: ownerState => {
              const isAvailable = availableDates?.some(availableDate =>
                availableDate.hasSame(ownerState.day, 'day'),
              );

              return {
                sx: {
                  ...(!isAvailable && {
                    color: 'text.disabled',
                    opacity: 0.5,
                  }),
                },
              };
            },
          }}
          onOpen={() => handleMonthChange(true)(parsedDateTimeFrom)}
          value={[parsedDateTimeFrom, parsedDateTimeTo]}
          onMonthChange={handleMonthChange(true)}
          disableAutoMonthSwitching
          onChange={handleDateRangeChange}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
    </Box>
  );
};

export default DatePicker;
